import {
  BasketIcon,
  BusIcon,
  CarIcon,
  CarrotIcon,
  ChartDonutIcon,
  CouchIcon,
  CowIcon,
  CaretRightIcon,
  CreditCardIcon,
  DotsThreeCircleIcon,
  FirstAidKitIcon,
  ForkKnifeIcon,
  GasPumpIcon,
  GiftIcon,
  HouseIcon,
  LightningIcon,
  MoneyIcon,
  PawPrintIcon,
  PiggyBankIcon,
  PillIcon,
  PlusIcon,
  PopcornIcon,
  ReceiptIcon,
  RepeatIcon,
  ScissorsIcon,
  ShieldCheckIcon,
  StudentIcon,
  TShirtIcon,
  TicketIcon,
  TrendUpIcon,
  UsersThreeIcon,
  WalletIcon,
  WifiHighIcon,
} from '@phosphor-icons/react'
import { type PlainDate, formatMoney, householdPeriod, sumBy, todayIn } from '@paybae/shared'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { DetalleMovimiento } from '@/components/DetalleMovimiento'
import { Card, EmptyState, SectionHeader } from '@/components/Card'
import { Donut, type DonutSegment, chartColor } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import { Money } from '@/components/Money'
import { PersonDot } from '@/components/PersonAvatar'
import { ScreenHeader } from '@/components/ScreenHeader'
import type { Transaction } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { personPalette } from '@/lib/persons'
import { useHousehold, useTransactions } from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Movimientos.
 *
 * Se agrupan por día y cada fila dice de un vistazo tres cosas: de quién fue el
 * gasto (el punto de color), con qué se pagó, y cuánto. Lo pagado con tarjeta
 * lleva una marca distinta porque no salió del efectivo todavía: mezclarlo con
 * lo demás haría creer que el dinero ya se fue.
 */

/**
 * Icono de cada categoría.
 *
 * La API guarda el icono como texto (`basket`, `fork-knife`…) para no atar la
 * base a una librería de iconos concreta, así que la traducción a componentes
 * vive aquí. Una categoría creada a mano puede traer un nombre que esta versión
 * de la app todavía no conozca, y por eso hay un neutro por defecto: mejor un
 * icono genérico que una fila rota.
 */
const ICONOS_CATEGORIA: Record<string, typeof BasketIcon> = {
  house: HouseIcon,
  car: CarIcon,
  lightning: LightningIcon,
  'wifi-high': WifiHighIcon,
  repeat: RepeatIcon,
  'shield-check': ShieldCheckIcon,
  student: StudentIcon,
  basket: BasketIcon,
  carrot: CarrotIcon,
  cow: CowIcon,
  'fork-knife': ForkKnifeIcon,
  'gas-pump': GasPumpIcon,
  bus: BusIcon,
  'first-aid-kit': FirstAidKitIcon,
  pill: PillIcon,
  popcorn: PopcornIcon,
  't-shirt': TShirtIcon,
  couch: CouchIcon,
  'paw-print': PawPrintIcon,
  gift: GiftIcon,
  scissors: ScissorsIcon,
  'dots-three-circle': DotsThreeCircleIcon,
  'credit-card': CreditCardIcon,
  'piggy-bank': PiggyBankIcon,
  money: MoneyIcon,
  ticket: TicketIcon,
  'trend-up': TrendUpIcon,
}

const ICONO_NEUTRO = ReceiptIcon

function iconoDeCategoria(icon: string | null | undefined): typeof BasketIcon {
  return (icon ? ICONOS_CATEGORIA[icon] : undefined) ?? ICONO_NEUTRO
}

/** `todos` = sin filtro, `joint` = fondo común, cualquier otra cosa = id de integrante. */
type Filtro = 'todos' | 'joint' | string

/**
 * Página máxima que acepta el servidor. Un mes de gastos de un hogar cabe de
 * sobra; si algún día no cupiera, el corte se ve en el contador del encabezado
 * antes que en una cifra equivocada.
 */
const POR_PAGINA = 100

export function Transactions() {
  const navigate = useNavigate()
  const [detalle, setDetalle] = useState<string | null>(null)
  const { membership } = useSession()
  // La fecha de calendario sale del huso del hogar, no del reloj del navegador:
  // a las 23:30 en México un teléfono en UTC ya estaría en el día siguiente y
  // el gasto de hoy aparecería bajo la fecha de mañana.
  const hoy = todayIn(membership.household.timezone)
  // Con el ancla del hogar, para que «este periodo» signifique aquí lo mismo que
  // en el tablero.
  const { periodStart, periodEnd } = householdPeriod(hoy, membership.household.periodAnchorDay)

  const [filtro, setFiltro] = useState<Filtro>('todos')

  const casa = useHousehold()
  const integrantes = casa.data?.members ?? []
  // Un rol limitado no puede consultar el fondo común ni lo ajeno: el servidor
  // responde 403. El chip no se dibuja en vez de ofrecer un filtro que falla.
  const veElHogar = membership.role === 'ADMIN' || membership.role === 'MEMBER'

  // El refiltrado lo hace el servidor. La lista de un hogar crece sin techo, y
  // traerla entera para descartar la mayor parte en memoria gastaría red y
  // batería en cada toque de un chip.
  const movimientos = useTransactions({
    from: periodStart,
    to: periodEnd,
    // Solo gastos: la dona dice «en qué se fue» y el encabezado «gastado». Un
    // ingreso o un traspaso entre cuentas propias inflaría ambas cifras con
    // dinero que no salió del hogar.
    type: 'EXPENSE',
    memberId: filtro === 'todos' ? undefined : filtro,
    pageSize: POR_PAGINA,
  })

  const items = useMemo(() => movimientos.data?.items ?? [], [movimientos.data])
  // El saldo con el que se dio de alta una tarjeta se lista —es un cargo real
  // de la tarjeta y hay que poder verlo—, pero no suma como gasto del mes: es
  // deuda de antes de usar la app. Con él, «En qué se fue» salía dominada por
  // un «Sin categoría» que nadie gastó este mes.
  const gastos = useMemo(() => items.filter((m) => !m.isOpeningBalance), [items])
  const total = sumBy(gastos, (m) => m.amountCents)
  const porDia = useMemo(() => agruparPorDia(items), [items])
  const porCategoria = useMemo(() => agruparPorCategoria(gastos), [gastos])

  // El subtítulo ocupa su renglón en los tres estados: si apareciera solo al
  // llegar los datos, el encabezado crecería y empujaría la lista hacia abajo
  // justo cuando el dedo ya está sobre ella.
  const subtitulo = movimientos.isPending
    ? 'Cargando…'
    : movimientos.isError
      ? 'No pudimos cargar el mes'
      : `${gastos.length} ${gastos.length === 1 ? 'gasto' : 'gastos'} este mes · ${formatMoney(total, { hideZeroCents: true })}`

  return (
    <>
      {/* Captura rápida en el encabezado, no flotando sobre la lista.
          Movimientos es justo donde uno está mirando cuando se acuerda de un
          gasto, y hasta ahora el único camino era volver al inicio. Va aquí y no
          como botón flotante para no invadir la cápsula de navegación ni tapar
          la última fila del día. */}
      <ScreenHeader
        title="Movimientos"
        subtitle={subtitulo}
        tinte="peach"
        action={
          <div className="flex shrink-0 items-center gap-2">
            {/* Los informes cuelgan de aquí y no de una pestaña propia: la
                pregunta «¿me pasé este mes?» nace mirando esta lista, no en un
                sitio al que haya que acordarse de ir. Va sin texto porque la
                cápsula de captura es la acción principal y dos etiquetas
                seguidas competirían por el mismo golpe de vista. */}
            <button
              type="button"
              onClick={() => {
                haptic('light')
                navigate('/informes')
              }}
              aria-label="Ver informes: el mes en curso y lo planeado contra lo real"
              className="flex size-10 shrink-0 items-center justify-center rounded-pill border border-line bg-surface text-ink-soft active:scale-95"
            >
              <ChartDonutIcon size={18} weight="fill" />
            </button>

            <button
              type="button"
              onClick={() => {
                haptic('light')
                navigate('/capturar')
              }}
              aria-label="Registrar un gasto"
              className={cn(
                'flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-pill bg-accion px-3.5',
                'text-[13px] font-semibold text-on-accion active:scale-95',
              )}
            >
              <PlusIcon size={16} weight="bold" />
              Gasto
            </button>
          </div>
        }
      />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
          <Chip activo={filtro === 'todos'} onClick={() => setFiltro('todos')}>
            Todos
          </Chip>
          {veElHogar ? (
            <Chip activo={filtro === 'joint'} onClick={() => setFiltro('joint')}>
              <UsersThreeIcon size={15} weight="fill" />
              Común
            </Chip>
          ) : null}
          {casa.isPending
            ? // Se reservan tantas pastillas como integrantes tendrá el hogar
              // según su tipo, para que la fila no se ensanche al cargar.
              Array.from({ length: chipsEsperados(membership.household.kind) }, (_, i) => (
                <Skeleton key={i} className="h-10 w-24 shrink-0 rounded-pill" />
              ))
            : integrantes.map((m) => (
                <Chip key={m.id} activo={filtro === m.id} onClick={() => setFiltro(m.id)}>
                  {/* El chip filtra por `m.id` y se colorea con `m.personColorToken`:
                      son dos cadenas distintas y confundirlas pintaría a todos igual. */}
                  <PersonDot token={m.personColorToken} />
                  {m.user.displayName.split(' ')[0]}
                </Chip>
              ))}
        </div>

        {movimientos.isPending ? (
          <Cargando />
        ) : movimientos.isError ? (
          <ErrorState
            titulo="No pudimos cargar tus movimientos"
            mensaje={
              movimientos.error instanceof Error
                ? movimientos.error.message
                : 'Inténtalo de nuevo.'
            }
            onReintentar={() => void movimientos.refetch()}
          />
        ) : (
          <>
            {porCategoria.length > 0 ? (
              <Card className="flex flex-col gap-4">
                <SectionHeader title="En qué se fue" className="mb-0" />
                <div className="flex items-center gap-4">
                  <Donut
                    segments={porCategoria}
                    centerValue={formatMoney(total, { hideZeroCents: true })}
                    centerLabel="Gastado"
                  />
                  <ul className="flex min-w-0 flex-1 flex-col gap-1.5">
                    {porCategoria.slice(0, 5).map((seg) => (
                      <li key={seg.key} className="flex items-baseline gap-2">
                        <span
                          aria-hidden="true"
                          className="size-2 shrink-0 translate-y-[-1px] rounded-full"
                          style={{ background: seg.color }}
                        />
                        <span className="line-clamp-2 text-[13px] leading-tight text-ink-soft">
                          {seg.label}
                        </span>
                        <span data-money className="ml-auto text-[13px] font-semibold text-ink">
                          {formatMoney(seg.value, { hideZeroCents: true })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ) : null}

            {porDia.length === 0 ? (
              <Card>
                {filtro === 'todos' ? (
                  <EmptyState
                    title="Nada por aquí todavía"
                    description="Registra tu primer gasto con el botón Gasto de arriba y aparecerá aquí al instante."
                    action={
                      <button
                        type="button"
                        onClick={() => {
                          haptic('light')
                          navigate('/capturar')
                        }}
                        className="flex min-h-[44px] items-center rounded-pill bg-accion px-5 text-[14px] font-semibold text-on-accion active:scale-95"
                      >
                        Registrar un gasto
                      </button>
                    }
                  />
                ) : (
                  <EmptyState
                    title="Sin gastos con este filtro"
                    description="Este mes no hay movimientos de quien elegiste. Quita el filtro para ver todo lo del hogar."
                    action={
                      <button
                        type="button"
                        onClick={() => {
                          haptic('light')
                          setFiltro('todos')
                        }}
                        className="-my-2.5 -mr-2 flex min-h-[44px] shrink-0 items-center rounded-card px-2 text-[13px] font-semibold text-primary-text active:scale-95"
                      >
                        Ver todos
                      </button>
                    }
                  />
                )}
              </Card>
            ) : (
              <div className="flex flex-col gap-4 pb-2">
                {porDia.map(([fecha, items]) => (
                  <section key={fecha}>
                    <h3 className="mb-1.5 px-1 text-[12px] font-medium uppercase tracking-[0.06em] text-ink-faint">
                      {etiquetaDia(fecha, hoy)}
                    </h3>
                    <Card className="divide-y divide-line p-0">
                      {items.map((m) => (
                        <MovimientoFila
                          key={m.id}
                          movimiento={m}
                          onAbrir={() => setDetalle(m.id)}
                        />
                      ))}
                    </Card>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* El detalle vive en una hoja y no en una pantalla: se consulta y se
          cierra, y la lista tiene que quedar donde estaba. */}
      <DetalleMovimiento id={detalle} onCerrar={() => setDetalle(null)} />
    </>
  )
}

function MovimientoFila({
  movimiento,
  onAbrir,
}: {
  movimiento: Transaction
  onAbrir: () => void
}) {
  // `attributedTo` nulo significa fondo común: no es de nadie en particular y
  // por eso va en neutro, no con el color del integrante que lo capturó.
  const owner = movimiento.attributedTo
  const palette = personPalette(owner?.personColorToken)
  const Icon = iconoDeCategoria(movimiento.category?.icon)

  const conTarjeta = movimiento.paymentSource === 'CREDIT_CARD'
  const conVales = movimiento.account?.type === 'MEAL_VOUCHER'
  const MedioIcon = conTarjeta ? CreditCardIcon : conVales ? BasketIcon : WalletIcon
  const medioLabel = conTarjeta
    ? `${movimiento.creditCard?.name ?? 'Tarjeta'}${
        movimiento.creditCard?.last4 ? ` ·· ${movimiento.creditCard.last4}` : ''
      }`
    : (movimiento.account?.name ?? 'Cuenta')

  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        onAbrir()
      }}
      className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-surface-sunken"
    >
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-full',
          owner ? palette.soft : 'bg-surface-sunken',
          owner ? palette.text : 'text-ink-soft',
        )}
      >
        <Icon size={18} weight="fill" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{movimiento.description}</p>
        <p className="flex items-center gap-1 truncate text-[12px] text-ink-soft">
          {owner ? <PersonDot token={owner.personColorToken} /> : null}
          {/* El saldo inicial dice qué es en vez de «Sin categoría»: sin
              categoría parecía un gasto olvidado por clasificar. */}
          {movimiento.isOpeningBalance
            ? 'Deuda al registrar la tarjeta'
            : (movimiento.category?.name ?? 'Sin categoría')}
          <span className="text-ink-faint">·</span>
          <MedioIcon size={12} />
          <span className="truncate">{medioLabel}</span>
        </p>
      </div>

      <div className="flex shrink-0 flex-col items-end">
        <Money cents={movimiento.amountCents} />
        {/* Lo cargado a tarjeta se marca aparte: todavía no salió del efectivo,
            y darlo por gastado sería el mismo error que la app quiere evitar. */}
        {movimiento.isOpeningBalance ? (
          <span className="text-[10px] text-ink-soft">no es gasto del mes</span>
        ) : conTarjeta ? (
          <span className="text-[10px] text-ink-faint">a crédito</span>
        ) : null}
      </div>
      <CaretRightIcon size={14} className="shrink-0 text-ink-faint" />
    </button>
  )
}

/**
 * Esqueleto de la lista.
 *
 * Reproduce la estructura real —la misma tarjeta, el mismo círculo de 120 px,
 * las mismas filas de 40 px de icono— en vez de un rectángulo aproximado: así
 * el contenido aterriza exactamente donde estaba el hueco y nada se mueve bajo
 * el dedo. Se dibuja también al cambiar de chip, que es cuando más se nota.
 */
function Cargando() {
  return (
    <>
      <Card className="flex flex-col gap-4">
        <SectionHeader title="En qué se fue" className="mb-0" />
        <div className="flex items-center gap-4">
          <Skeleton className="size-[120px] shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-[15px] w-full" />
            ))}
          </div>
        </div>
      </Card>

      <div className="flex flex-col gap-4 pb-2">
        {[3, 2].map((filas, seccion) => (
          <section key={seccion}>
            <Skeleton className="mb-1.5 ml-1 h-[15px] w-20" />
            <Card className="divide-y divide-line p-0">
              {Array.from({ length: filas }, (_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="size-10 shrink-0 rounded-full" />
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <Skeleton className="h-[14px] w-2/5" />
                    <Skeleton className="h-[12px] w-3/5" />
                  </div>
                  <Skeleton className="h-[14px] w-16 shrink-0" />
                </div>
              ))}
            </Card>
          </section>
        ))}
      </div>
    </>
  )
}

function Chip({
  activo,
  onClick,
  children,
}: {
  activo: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('selection')
        onClick()
      }}
      aria-pressed={activo}
      className={cn(
        'flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-pill border px-3.5 text-[13px] font-medium',
        'transition-colors duration-150 active:scale-95',
        activo ? 'border-accion bg-accion text-on-accion' : 'border-line bg-surface text-ink-soft',
      )}
    >
      {children}
    </button>
  )
}

/** Cuántas pastillas de integrante reservar mientras carga el hogar. */
function chipsEsperados(kind: 'PERSONAL' | 'COUPLE' | 'GROUP'): number {
  return kind === 'PERSONAL' ? 1 : kind === 'COUPLE' ? 2 : 3
}

function agruparPorDia(items: readonly Transaction[]): Array<[PlainDate, Transaction[]]> {
  const mapa = new Map<PlainDate, Transaction[]>()
  for (const item of items) {
    const lista = mapa.get(item.occurredOn)
    if (lista) lista.push(item)
    else mapa.set(item.occurredOn, [item])
  }
  // El orden de cadena de una fecha ISO es el cronológico, así que ordenar
  // descendente no necesita convertir nada a Date.
  return [...mapa.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1))
}

/** Gasto por categoría, de mayor a menor: la dona se lee mejor ordenada. */
function agruparPorCategoria(items: readonly Transaction[]): DonutSegment[] {
  const mapa = new Map<string, DonutSegment>()
  for (const item of items) {
    const nombre = item.category?.name ?? 'Sin categoría'
    const previo = mapa.get(nombre)
    if (previo) previo.value += item.amountCents
    else
      mapa.set(nombre, {
        key: nombre,
        label: nombre,
        value: item.amountCents,
        color: 'var(--chart-1)',
      })
  }
  // Se ordena antes de colorear: así el gasto mayor siempre recibe el primer
  // color de la rampa y la lectura es estable entre meses.
  return [...mapa.values()]
    .sort((a, b) => b.value - a.value)
    .map((seg, i) => ({ ...seg, color: chartColor(i) }))
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

function etiquetaDia(fecha: PlainDate, hoy: PlainDate): string {
  if (fecha === hoy) return 'Hoy'
  const [year, month, day] = fecha.split('-')
  const [hoyYear] = hoy.split('-')
  const base = `${Number(day)} de ${MESES[Number(month) - 1]}`
  return year === hoyYear ? base : `${base} ${year}`
}
